// hooks/useAuth.js
import { useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  deleteUser,
  reauthenticateWithPopup
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { getBeltForSessions } from '../constants/beltRanks';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsUsername, setNeedsUsername] = useState(false);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setError(null);
      if (!firebaseUser) {
        setUser(null);
        setNeedsUsername(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const profileRef = doc(db, 'profiles', firebaseUser.uid);
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists() && profileSnap.data().username) {
          setUser(profileSnap.data());
          setNeedsUsername(false);
        } else {
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            profileImage: firebaseUser.photoURL || null
          });
          setNeedsUsername(true);
        }
      } catch (err) {
        console.error('Auth state error', err);
        setError(err.message || 'Authentication error');
        // Keep current user state to avoid bouncing to login on transient errors
        setNeedsUsername(false);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    try {
      setError(null);
      
      // Additional error handling for storage issues
      try {
        // Test if BOTH localStorage and sessionStorage are accessible
        // Firebase OAuth needs sessionStorage during the auth flow
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        sessionStorage.setItem('test', 'test');
        sessionStorage.removeItem('test');
      } catch (storageErr) {
        throw new Error('Browser storage is blocked. Please enable cookies and site data for this website.');
      }
      
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      const profileRef = doc(db, 'profiles', firebaseUser.uid);
      const profileSnap = await getDoc(profileRef);

      if (profileSnap.exists() && profileSnap.data().username) {
        setUser(profileSnap.data());
        setNeedsUsername(false);
      } else {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          profileImage: firebaseUser.photoURL || null
        });
        setNeedsUsername(true);
      }
    } catch (err) {
      console.error('Login error', err);
      
      // Provide more user-friendly error messages
      let errorMsg = 'Login failed';
      if (err.code === 'auth/popup-blocked') {
        errorMsg = 'Popup was blocked. Please allow popups for this site.';
      } else if (err.code === 'auth/popup-closed-by-user') {
        errorMsg = 'Login cancelled. Please try again.';
      } else if (err.code === 'auth/unauthorized-domain') {
        errorMsg = 'This domain is not authorized. Please contact support.';
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const setUsername = async (username, profileImage) => {
    if (!auth.currentUser) throw new Error('Not signed in');
    const normalized = username.trim().toLowerCase();
    if (!normalized) throw new Error('Username required');

    const uid = auth.currentUser.uid;
    const email = auth.currentUser.email;
    const displayName = auth.currentUser.displayName;
    const fallbackPhoto = auth.currentUser.photoURL || null;

    const usernameRef = doc(db, 'usernames', normalized);
    const profileRef = doc(db, 'profiles', uid);

    setError(null);
    await runTransaction(db, async (tx) => {
      const existingUsername = await tx.get(usernameRef);
      if (existingUsername.exists() && existingUsername.data().uid !== uid) {
        throw new Error('Username is taken');
      }

      tx.set(usernameRef, { uid });
      tx.set(profileRef, {
        uid,
        email,
        displayName,
        username,
        usernameLower: normalized,
        profileImage: profileImage || fallbackPhoto,
        sessionCount: 0,
        workoutCount: 0,
        beltRank: getBeltForSessions(0).name,
        createdAt: serverTimestamp()
      }, { merge: true });
    });

    const profileSnap = await getDoc(profileRef);
    setUser(profileSnap.data());
    setNeedsUsername(false);
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setNeedsUsername(false);
    setError(null);
    localStorage.removeItem('fitBudly_sessions');
  };

  const deleteProfile = async () => {
    if (!auth.currentUser) throw new Error('Not signed in');
    if (isDeleting) throw new Error('Delete already in progress');
    
    setIsDeleting(true);
    const uid = auth.currentUser.uid;

    const deleteUserData = async () => {
      const profileRef = doc(db, 'profiles', uid);
      const profileSnap = await getDoc(profileRef);
      const usernameLower = profileSnap.exists() ? profileSnap.data().usernameLower : null;

      // Delete each collection separately to isolate permission errors
      try {
        console.log('Deleting profile:', uid);
        await deleteDoc(profileRef);
        console.log('Profile deleted');
      } catch (err) {
        console.error('Failed to delete profile:', err);
        throw new Error(`Cannot delete profile: ${err.message}`);
      }

      if (usernameLower) {
        try {
          console.log('Deleting username:', usernameLower);
          await deleteDoc(doc(db, 'usernames', usernameLower));
          console.log('Username deleted');
        } catch (err) {
          console.error('Failed to delete username:', err);
          // Don't throw, continue cleanup
        }
      }

      // Sessions subcollection
      try {
        console.log('Deleting sessions for:', uid);
        const sessionsSnap = await getDocs(collection(db, 'users', uid, 'sessions'));
        console.log('Found sessions:', sessionsSnap.size);
        for (const sessionDoc of sessionsSnap.docs) {
          await deleteDoc(sessionDoc.ref);
        }
        console.log('Sessions deleted');
      } catch (err) {
        console.error('Failed to delete sessions:', err);
      }

      // Friends relationships
      try {
        console.log('Deleting friend relationships for:', uid);
        const friendsRef = collection(db, 'friends');
        const [friendsA, friendsB] = await Promise.all([
          getDocs(query(friendsRef, where('uidA', '==', uid))),
          getDocs(query(friendsRef, where('uidB', '==', uid)))
        ]);
        console.log('Found friend docs as uidA:', friendsA.size, 'as uidB:', friendsB.size);
        
        for (const friendDoc of friendsA.docs) {
          const data = friendDoc.data();
          if (data?.uidA === uid || data?.uidB === uid) {
            await deleteDoc(friendDoc.ref);
          }
        }
        for (const friendDoc of friendsB.docs) {
          const data = friendDoc.data();
          if (data?.uidA === uid || data?.uidB === uid) {
            await deleteDoc(friendDoc.ref);
          }
        }
        console.log('Friend relationships deleted');
      } catch (err) {
        console.error('Failed to delete friend relationships:', err);
      }

      // Friend requests
      try {
        console.log('Deleting friend requests for:', uid);
        const friendRequestsRef = collection(db, 'friendRequests');
        const [reqFrom, reqTo] = await Promise.all([
          getDocs(query(friendRequestsRef, where('fromUid', '==', uid))),
          getDocs(query(friendRequestsRef, where('toUid', '==', uid)))
        ]);
        console.log('Found friend requests from user:', reqFrom.size, 'to user:', reqTo.size);
        
        for (const reqDoc of reqFrom.docs) {
          const data = reqDoc.data();
          if (data?.fromUid === uid || data?.toUid === uid) {
            await deleteDoc(reqDoc.ref);
          }
        }
        for (const reqDoc of reqTo.docs) {
          const data = reqDoc.data();
          if (data?.fromUid === uid || data?.toUid === uid) {
            await deleteDoc(reqDoc.ref);
          }
        }
        console.log('Friend requests deleted');
      } catch (err) {
        console.error('Failed to delete friend requests:', err);
      }

      // Notification preferences owned by the user
      try {
        console.log('Deleting notification prefs for:', uid);
        const prefsRef = collection(db, 'notificationPreferences');
        const [prefsUser] = await Promise.all([
          getDocs(query(prefsRef, where('userId', '==', uid)))
        ]);
        console.log('Found notification prefs:', prefsUser.size);
        
        for (const prefDoc of prefsUser.docs) {
          if (prefDoc.id.startsWith(`${uid}_`)) {
            await deleteDoc(prefDoc.ref);
          }
        }
        console.log('Notification prefs deleted');
      } catch (err) {
        // Notification prefs are optional - just warn, don't block deletion
        console.warn('Could not delete notification prefs (this is OK):', err.message);
      }
    };
    
    try {
      setError(null);
      // Delete Firestore data first, then auth account
      await deleteUserData();
      await deleteUser(auth.currentUser);

      setUser(null);
      setNeedsUsername(false);
      localStorage.removeItem('fitBudly_sessions');
    } catch (err) {
      // If requires recent login, reauthenticate with Google and retry
      if (err.code === 'auth/requires-recent-login') {
        try {
          console.log('Reauthenticating with Google...');
          await reauthenticateWithPopup(auth.currentUser, googleProvider);

          await deleteUserData();
          await deleteUser(auth.currentUser);

          setUser(null);
          setNeedsUsername(false);
          localStorage.removeItem('fitBudly_sessions');
        } catch (reAuthErr) {
          if (reAuthErr.code === 'auth/cancelled-popup-request') {
            console.warn('Reauthentication popup was cancelled');
            setError('You cancelled the sign-in. Please try again to delete your profile.');
          } else {
            console.error('Reauthentication failed', reAuthErr);
            setError(reAuthErr.message || 'Reauthentication failed. Please try again.');
          }
          throw reAuthErr;
        }
      } else {
        console.error('Delete profile error', err);
        setError(err.message || 'Failed to delete profile');
        throw err;
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    user,
    loading,
    needsUsername,
    error,
    loginWithGoogle,
    setUsername,
    logout,
    deleteProfile
  };
}